/**
 * Axis-B org-scope resolver (S0.3) — the pure `WHERE` half of the two-axis RBAC
 * model. Given a principal's scoped `assignments` and the org tree, it resolves
 * the set of org units whose transactional rows the principal may see. Zero-I/O,
 * no globals, no `Date.now`/`Math.random`, no React, no MSW — the caller supplies
 * the full dataset — so the MSW list/detail handlers (S1.4) and the UI guard
 * consume it VERBATIM and can never drift (the same shared-selector discipline as
 * `src/lib/occ/selectors.ts`, `src/lib/pricing/resolvePrice.ts`,
 * `src/lib/reorder/computeReorderSuggestion.ts`). Composed by
 * `resolveEffectiveAccess` (S0.4) after Axis-A grants are known.
 *
 * The model (plan §0 · Axis B — WHERE):
 *   - A grant scoped at org X reaches DOWN the org tree: `self` = just X;
 *     `self_and_subtree` = X and every descendant; `subtree_unassigned` = that
 *     subtree minus the branches the SAME role already covers via a lower
 *     assignment (Workday dedup — designed-in, owner-gated OFF, Open Q5).
 *   - Scope only ever NARROWS access, never grants it (SAP intersection): a row is
 *     visible iff its `orgId` is in this set OR the row is shared HQ master data
 *     (`orgId == null`, always visible — Odoo pattern).
 *   - `model === 'franchise'` (加盟) is a DESCENT-STOP closed compartment: an
 *     ancestor grant does NOT descend INTO a franchise subtree (HQ does not see AU
 *     franchise rows) unless the principal holds the `crossFranchiseView` Axis-A
 *     grant. A principal anchored AT a franchise node still sees its own subtree —
 *     the anchor's own model is never a boundary, only a franchise node reached
 *     while descending from above closes the compartment.
 *   - `status === 'inactive'` orgs are excluded from every resolved set.
 *
 * Materialized-path note: the model documents a denormalized `OrganisationUnit.path`
 * (e.g. `HQ/TW`) for Axis-B prefix scoping, but keeps **adjacency (`parentId`) as
 * the source of truth** (MSW recomputes `path` on create/re-parent, S2.1). This
 * resolver realizes the materialized-path prefix relation DIRECTLY on the adjacency
 * source of truth — id-based, so it is collision-free and immune to a stale `path`
 * after a re-parent — which is behaviour-identical to a `path`-prefix test whenever
 * `path` is in sync. No `path` field is read here.
 */

import type { PrincipalIdentity, RoleAssignment } from './types';

// ---------------------------------------------------------------------------
// Structural input — the minimal org shape this resolver reads
// ---------------------------------------------------------------------------

/**
 * The minimal org-node shape Axis-B needs. Declared structurally (not importing
 * `OrganisationUnit` from the components layer) so the pure resolver never
 * depends UP on `src/components`, yet a full `OrganisationUnit[]` is assignable
 * verbatim. `model`/`status` are widened to `string` so the resolver stays robust
 * to the domain unions growing (`OrgModel` / `GovernanceRecordStatus`) — only the
 * `'franchise'` and `'active'` literals are load-bearing here.
 */
export interface OrgScopeNode {
  id: string;
  /** Adjacency source of truth — absent on the root. */
  parentId?: string;
  /** `'direct'`(直營) | `'franchise'`(加盟); only `'franchise'` is a descent-stop. */
  model: string;
  /** `'active'` | `'inactive'`; inactive orgs are excluded from every set. */
  status: string;
}

/** Options that carry Axis-A facts the Axis-B expansion needs. */
export interface OrgScopeOptions {
  /**
   * The principal holds the `crossFranchiseView` grant. S0.4 supplies this from
   * the resolved Axis-A grants (`grants.has('governance.crossFranchiseView')`);
   * kept as a boolean here so Axis B stays decoupled from the permission catalog.
   * When `true`, franchise subtrees stop being a descent-stop and are reached like
   * any direct subtree.
   */
  hasCrossFranchiseView?: boolean;
}

/** The one org model value that closes a franchise compartment (加盟). */
const FRANCHISE_MODEL = 'franchise';
/** Only active orgs enter a resolved visible set. */
const ACTIVE_STATUS = 'active';

// ---------------------------------------------------------------------------
// Adjacency helpers — the source-of-truth realization of the path-prefix tests
// ---------------------------------------------------------------------------

function indexById(orgs: readonly OrgScopeNode[]): Map<string, OrgScopeNode> {
  const byId = new Map<string, OrgScopeNode>();
  for (const org of orgs) byId.set(org.id, org);
  return byId;
}

/**
 * Is `node` the anchor itself or a descendant of it? Walks `node`'s parent chain
 * to the root looking for `anchorId` — the id-based realization of
 * `node.path` startsWith `anchor.path` (collision-free, no strings). Cycle-guarded.
 */
function isDescendantOrSelf(
  node: OrgScopeNode | undefined,
  anchorId: string,
  byId: Map<string, OrgScopeNode>
): boolean {
  let cur = node;
  const seen = new Set<string>();
  while (cur) {
    if (cur.id === anchorId) return true;
    if (seen.has(cur.id)) return false; // defensive cycle guard on malformed data
    seen.add(cur.id);
    cur = cur.parentId ? byId.get(cur.parentId) : undefined;
  }
  return false;
}

/**
 * Descending from `anchorId` toward a descendant `node`, is a franchise boundary
 * crossed? True iff any node on the chain from `node` up to — but NOT including —
 * the anchor is a franchise node (the anchor's own model is never a boundary, so a
 * principal anchored at a franchise still sees its own subtree). Only meaningful
 * for a `node` that is already a strict descendant of the anchor.
 */
function crossesFranchiseBoundary(
  node: OrgScopeNode,
  anchorId: string,
  byId: Map<string, OrgScopeNode>
): boolean {
  let cur: OrgScopeNode | undefined = node;
  const seen = new Set<string>();
  while (cur && cur.id !== anchorId) {
    if (cur.model === FRANCHISE_MODEL) return true;
    if (seen.has(cur.id)) return false; // defensive cycle guard
    seen.add(cur.id);
    cur = cur.parentId ? byId.get(cur.parentId) : undefined;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Per-mode expansion
// ---------------------------------------------------------------------------

/** `self` — just the anchor, when it exists and is active. */
function expandSelf(anchorId: string, byId: Map<string, OrgScopeNode>): Set<string> {
  const anchor = byId.get(anchorId);
  return anchor && anchor.status === ACTIVE_STATUS ? new Set([anchorId]) : new Set();
}

/**
 * `self_and_subtree` — the anchor and every active descendant reachable without
 * crossing a franchise boundary (unless `hasCrossFranchiseView`). HQ-root anchor
 * ⇒ the whole active tree (minus any walled franchise compartment).
 */
function expandSubtree(
  anchorId: string,
  orgs: readonly OrgScopeNode[],
  byId: Map<string, OrgScopeNode>,
  hasCrossFranchiseView: boolean
): Set<string> {
  const out = new Set<string>();
  for (const org of orgs) {
    if (!isDescendantOrSelf(org, anchorId, byId)) continue;
    if (org.status !== ACTIVE_STATUS) continue;
    if (
      org.id !== anchorId &&
      !hasCrossFranchiseView &&
      crossesFranchiseBoundary(org, anchorId, byId)
    ) {
      continue; // franchise descent-stop: the compartment is closed from above
    }
    out.add(org.id);
  }
  return out;
}

/**
 * The org footprint a single assignment covers, used by `subtree_unassigned`
 * dedup: `self` covers its anchor; every subtree mode covers its subtree.
 */
function assignmentFootprint(
  assignment: RoleAssignment,
  identity: PrincipalIdentity,
  orgs: readonly OrgScopeNode[],
  byId: Map<string, OrgScopeNode>,
  hasCrossFranchiseView: boolean
): Set<string> {
  const anchorId = assignment.orgId || identity.homeOrgId;
  if (!anchorId) return new Set();
  return assignment.mode === 'self'
    ? expandSelf(anchorId, byId)
    : expandSubtree(anchorId, orgs, byId, hasCrossFranchiseView);
}

/**
 * The visible-org set contributed by ONE assignment. `subtree_unassigned`
 * (owner-gated OFF, designed-in — plan §Appendix A#3 / Open Q5) starts from the
 * `self_and_subtree` set and subtracts the footprint of every OTHER assignment of
 * the SAME role anchored at a strict descendant — the pure-resolver realization of
 * Workday's "subtree except branches already assigned lower down" (dedup is scoped
 * to THIS principal's own assignments; a global "assigned elsewhere" fact would
 * need a broader input, out of scope in v1).
 */
export function expandAssignmentScope(
  assignment: RoleAssignment,
  identity: PrincipalIdentity,
  orgs: readonly OrgScopeNode[],
  options: OrgScopeOptions = {}
): Set<string> {
  const hasCrossFranchiseView = options.hasCrossFranchiseView === true;
  const byId = indexById(orgs);
  const anchorId = assignment.orgId || identity.homeOrgId;
  if (!anchorId || !byId.has(anchorId)) return new Set();

  switch (assignment.mode) {
    case 'self':
      return expandSelf(anchorId, byId);
    case 'self_and_subtree':
      return expandSubtree(anchorId, orgs, byId, hasCrossFranchiseView);
    case 'subtree_unassigned': {
      const reach = expandSubtree(anchorId, orgs, byId, hasCrossFranchiseView);
      for (const other of identity.assignments ?? []) {
        if (other === assignment || other.roleId !== assignment.roleId) continue;
        const otherAnchorId = other.orgId || identity.homeOrgId;
        if (!otherAnchorId || otherAnchorId === anchorId) continue;
        const otherAnchor = byId.get(otherAnchorId);
        // strict descendant of the anchor (self excluded above)
        if (!otherAnchor || !isDescendantOrSelf(otherAnchor, anchorId, byId)) continue;
        for (const id of assignmentFootprint(other, identity, orgs, byId, hasCrossFranchiseView)) {
          reach.delete(id);
        }
      }
      return reach;
    }
    default:
      return new Set();
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Resolve the full Axis-B visible-org set for a principal — the union of every
 * assignment's per-mode reach, franchise-walled and inactive-filtered. Empty
 * `assignments` ⇒ an empty set: the degenerate flat-union path applies no Axis-B
 * scoping, and the flag-gated handlers (S1.4) simply do not filter — so a
 * flag-OFF principal is never hidden by an empty set (see `isRecordVisible`).
 */
export function resolveVisibleOrgIds(
  identity: PrincipalIdentity,
  orgs: readonly OrgScopeNode[],
  options: OrgScopeOptions = {}
): Set<string> {
  const visible = new Set<string>();
  for (const assignment of identity.assignments ?? []) {
    for (const id of expandAssignmentScope(assignment, identity, orgs, options)) {
      visible.add(id);
    }
  }
  return visible;
}

/**
 * Is a row visible under Axis B? Shared HQ master data (`orgId == null` /
 * `undefined` — products, vendors, taxonomy) is ALWAYS visible (Odoo pattern);
 * an org-stamped transactional row is visible iff its `orgId` is in the resolved
 * set. The single predicate both the list handlers (S1.4) and the UI read, so a
 * row can never be shown one place and hidden another.
 */
export function isRecordVisible(
  orgId: string | null | undefined,
  visibleOrgIds: ReadonlySet<string>
): boolean {
  if (orgId == null) return true;
  return visibleOrgIds.has(orgId);
}
